import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  RefreshCw,
  Search,
  Loader2,
  Truck,
  Eye,
  DollarSign,
  Plus,
} from 'lucide-react';
import Layout from '../components/Layout';
import subcontractService from '../services/subcontractService';
import BatchCreateSubcontractDialog from '../components/subcontract/BatchCreateSubcontractDialog';

function SubcontractPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = { page, page_size: pageSize };
      if (searchTerm) params.search = searchTerm;
      if (statusFilter) params.status = statusFilter;

      const response = await subcontractService.getSubcontractOrders(params);
      setOrders(response.items || response.data?.items || []);
      setTotalItems(response.total || response.data?.total || 0);
    } catch (err) {
      console.error('Failed to load subcontract orders:', err);
      setError('加载外协单失败，请重试');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchTerm, statusFilter]);

  const loadStats = useCallback(async () => {
    try {
      const statsData = await subcontractService.getSubcontractStats();
      setStats(statsData.data || statsData);
    } catch (err) {
      console.error('Failed to load subcontract stats:', err);
    }
  }, []);

  useEffect(() => {
    loadOrders();
    loadStats();
  }, [loadOrders, loadStats, refreshTrigger]);

  const handleRefresh = () => setRefreshTrigger(prev => prev + 1);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handleStatusFilter = (value) => {
    setStatusFilter(value === 'all' ? '' : value);
    setPage(1);
  };

  const handleViewOrder = (orderId) => {
    navigate(`/subcontract/${orderId}`);
  };

  const getStatusBadge = (status) => {
    const config = {
      draft: { variant: 'secondary', label: '草稿' },
      issued: { variant: 'warning', label: '已发料' },
      in_progress: { variant: 'default', label: '加工中' },
      completed: { variant: 'success', label: '已完成' },
      cancelled: { variant: 'destructive', label: '已取消' },
    };
    const { variant, label } = config[status] || { variant: 'secondary', label: status };
    return <Badge variant={variant}>{label}</Badge>;
  };

  const getSettlementBadge = (status) => {
    if (status === 'settled') return <Badge variant="success">已结算</Badge>;
    return <Badge variant="outline">待结算</Badge>;
  };

  const totalPages = Math.ceil(totalItems / pageSize);

  return (
    <Layout>
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Truck className="h-8 w-8 text-amber-600" />
            <div>
              <h1 className="text-2xl font-bold">外协管理</h1>
              <p className="text-sm text-muted-foreground">管理外协加工单、追踪工序外包进度</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="default"
              onClick={() => setBatchDialogOpen(true)}
              className="bg-amber-600 hover:bg-amber-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              批量创建外协
            </Button>
            <Button variant="outline" onClick={handleRefresh} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              刷新
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[
              { label: '草稿', key: 'draft', color: 'bg-gray-100' },
              { label: '已发料', key: 'issued', color: 'bg-amber-50' },
              { label: '加工中', key: 'in_progress', color: 'bg-blue-50' },
              { label: '已完成', key: 'completed', color: 'bg-green-50' },
            ].map(({ label, key, color }) => {
              const stat = stats.by_status?.[key];
              return (
                <Card key={key} className={color}>
                  <CardContent className="p-4">
                    <div className="text-2xl font-bold">{stat?.count || 0}</div>
                    <div className="text-sm text-muted-foreground">{label}</div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="搜索外协单号、供应商、工单号..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="pl-9"
                />
              </div>
              <Select value={statusFilter || 'all'} onValueChange={handleStatusFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="全部状态" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部状态</SelectItem>
                  <SelectItem value="draft">草稿</SelectItem>
                  <SelectItem value="issued">已发料</SelectItem>
                  <SelectItem value="in_progress">加工中</SelectItem>
                  <SelectItem value="completed">已完成</SelectItem>
                  <SelectItem value="cancelled">已取消</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Error */}
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>外协单号</TableHead>
                  <TableHead>供应商</TableHead>
                  <TableHead>工序</TableHead>
                  <TableHead className="text-center">数量</TableHead>
                  <TableHead className="text-right">金额</TableHead>
                  <TableHead className="text-center">状态</TableHead>
                  <TableHead className="text-center">结算</TableHead>
                  <TableHead className="text-center">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && orders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                      <p className="text-sm text-muted-foreground mt-2">加载中...</p>
                    </TableCell>
                  </TableRow>
                ) : orders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      暂无外协单
                    </TableCell>
                  </TableRow>
                ) : (
                  orders.map((order) => (
                    <TableRow key={order._id} className="cursor-pointer hover:bg-gray-50" onClick={() => handleViewOrder(order.subcontract_order_id)}>
                      <TableCell className="font-medium">{order.subcontract_order_id}</TableCell>
                      <TableCell>{order.supplier_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{order.step_name}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {order.total_completed_quantity > 0 ? (
                          <span>{order.total_completed_quantity} / {order.total_planned_quantity}</span>
                        ) : (
                          <span>{order.total_planned_quantity}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <DollarSign className="h-3 w-3 text-muted-foreground" />
                          {order.settlement_amount > 0 ? (
                            <span>{order.settlement_amount.toFixed(2)}</span>
                          ) : (
                            <span>{order.total_amount?.toFixed(2)}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">{getStatusBadge(order.status)}</TableCell>
                      <TableCell className="text-center">{getSettlementBadge(order.settlement_status)}</TableCell>
                      <TableCell className="text-center">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewOrder(order.subcontract_order_id);
                          }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-muted-foreground">
              共 {totalItems} 条记录，第 {page}/{totalPages} 页
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1 || loading}
              >
                上一页
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages || loading}
              >
                下一页
              </Button>
            </div>
          </div>
        )}
      </div>

      <BatchCreateSubcontractDialog
        open={batchDialogOpen}
        onOpenChange={setBatchDialogOpen}
        onSuccess={() => {
          setBatchDialogOpen(false);
          handleRefresh();
        }}
      />
    </Layout>
  );
}

export default SubcontractPage;
